import { SearchCarUnderuseEventCondition, CarUnderuseAnswersEditRequest } from "@/models/carUnderuseEvents/underuse";
import { Office } from "@/models/office";

export const CarUnderuseEventsConstants = {
  CarUnderuseEvents: "月次の稼働確認",
  CarUnderuseAnswers: "理由登録履歴へ",
  CompanyOffice: "会社/営業所",
  Company: "会社",
  Office: "営業所",
  RadioNumber: "無線番号",
  PlateNumber: "車両ナンバー",
  YearMonth: "対象年月度",
  EventAt: "検出日時",
  Edit: "未稼働/低稼働車両",
  Back: "戻る",
  MonthlyRunKm: "当月の走行距離(km)",
  UnderuseReason: "未稼働・低稼働理由",
  RestartPlanDate: "稼働再開予定日",
  RestartPlanDetail: "未稼働・低稼働理由と見通しの詳細",
  DetailPlaceHolder: "この欄に理由と見通しの詳細をご記入ください",
  Register: "登録",
  Confirm: "登録内容確認",
  ConfirmMessage: "入力された内容で登録します",
} as const;

export type CarUnderuseEventsEditState = {
  underuseReason: string;
  restartPlanDate?: string;
  restartPlanDetail: string;
}

export type SavedState = {
  officesSelected: Office[];
  page: number;
  searchCondition: SearchCarUnderuseEventCondition;
};

export const PAGE_SIZE = 30;

export const resolveEditRequest = (editState: CarUnderuseEventsEditState): CarUnderuseAnswersEditRequest => {
  return {
    underuseReason: getUnderuseReasonCode(editState.underuseReason ?? '') ?? '',
    restartPlanDate: editState.restartPlanDate ?? '',
    restartPlanDetail: editState.restartPlanDetail,
  }
}

export const getEditWarnMessage = (editState: CarUnderuseEventsEditState) => {
  return `${CarUnderuseEventsConstants.UnderuseReason}：${editState.underuseReason ?? "未選択"}
${CarUnderuseEventsConstants.RestartPlanDate}：${editState.restartPlanDate ?? "未選択"}

${CarUnderuseEventsConstants.ConfirmMessage}`;
}

// 年月度を「yyyy年MM月度」形式で表示する関数
export const formatYearMonth = (yearMonth: string): string => {
  if (!yearMonth || yearMonth.length !== 7) return yearMonth;

  const year = yearMonth.substring(0, 4);
  const month = yearMonth.substring(5, 7);
  return `${year}年${month}月`;
};

export type CarUnderuseEventsEditError = {
  underuseReason?: boolean;
  restartPlanDate?: boolean;
  planDescription?: boolean;
}

export const UnderuseReasonCodes = {
  ACCIDENT_WITH_REPORT: "ACCIDENT_WITH_REPORT",
  ACCIDENT_WITHOUT_REPORT: "ACCIDENT_WITHOUT_REPORT",
  BREAKDOWN_NOT_ACCIDENT: "BREAKDOWN_NOT_ACCIDENT",
  OTHER: "OTHER",
  UNDEFINED: "UNDEFINED",
} as const;

export const UnderuseReasonLabels = {
  ACCIDENT_WITH_REPORT: "事故修理（報告フォーム提出済）",
  ACCIDENT_WITHOUT_REPORT: "事故修理（報告フォーム未提出）",
  BREAKDOWN_NOT_ACCIDENT: "事故ではない故障修理",
  OTHER: "事故や故障以外",
  UNDEFINED: "",
} as const;

export type UnderuseReasonCode = keyof typeof UnderuseReasonCodes;

export const UNDERUSE_REASONS: { label: string, code: UnderuseReasonCode }[] = [
  { label: UnderuseReasonLabels.ACCIDENT_WITH_REPORT, code: UnderuseReasonCodes.ACCIDENT_WITH_REPORT },
  { label: UnderuseReasonLabels.ACCIDENT_WITHOUT_REPORT, code: UnderuseReasonCodes.ACCIDENT_WITHOUT_REPORT },
  { label: UnderuseReasonLabels.BREAKDOWN_NOT_ACCIDENT, code: UnderuseReasonCodes.BREAKDOWN_NOT_ACCIDENT },
  { label: UnderuseReasonLabels.OTHER, code: UnderuseReasonCodes.OTHER },
];

export const getUnderuseReasonLabel = (code: string): string => {
  return UnderuseReasonLabels[code as keyof typeof UnderuseReasonLabels];
}

export const getUnderuseReasonCode = (label: string): string => {
  return UNDERUSE_REASONS.find(r => r.label === label)?.code || UnderuseReasonCodes.UNDEFINED;
};

export const INITIAL_EDIT_STATE: CarUnderuseEventsEditState = {
  underuseReason: getUnderuseReasonLabel(UnderuseReasonCodes.UNDEFINED),
  restartPlanDate: undefined,
  restartPlanDetail: '',
}

export const ACCIDENT_EXAMPLE =
  `【理由】
1月1日に事故が発生して、現在修理中。

【見通し】
現在、ディーラー工場に修理を預けているものの、工場側で修理に時間がかかり、3月1日から稼働を再開できる見込み。
`;

export const BREAKDOWN_EXAMPLE =
  `【理由】
1月1日にエンジンが故障（異音が発生）して、現在修理中。

【見通し】
現在、ディーラー工場に修理を預けているものの、工場側で修理に時間がかかり、3月1日から稼働を再開できる見込み。
`;

export const OTHER_EXAMPLE_1 =
  `【理由】
1月1日〜15日に乗務員が不在で、稼働できない期間があった。

【見通し】
現在、乗務員は戻ってきていて、1月20日から稼働を再開できる見込み。
`

export const OTHER_EXAMPLE_2 =
  `【理由】
1月1日に乗務員が退職して、稼働できなくなってしまった。

【見通し】
現在、別車両から乗務員を回しており、1月20日から稼働を再開できる見込み。
`;

export const OTHER_EXAMPLE_3 =
  `【理由】
Reserve、NRSでの調整により乗務員のシフトが空いてしまい、稼働が難しい状況となっている。

【見通し】
○日の乗車サイクルに合わせて調整中で、現在の予定では ○曜日・○曜日は乗務可能。
そのため、○月○日より通常稼働を再開できる見込み。
`;

const RESTART_PLAN_DETAIL_EXAMPLE: Record<string, string[]> = {
  [UnderuseReasonCodes.ACCIDENT_WITH_REPORT]: [ACCIDENT_EXAMPLE],
  [UnderuseReasonCodes.ACCIDENT_WITHOUT_REPORT]: [ACCIDENT_EXAMPLE],
  [UnderuseReasonCodes.BREAKDOWN_NOT_ACCIDENT]: [BREAKDOWN_EXAMPLE],
  [UnderuseReasonCodes.OTHER]: [OTHER_EXAMPLE_1, OTHER_EXAMPLE_2, OTHER_EXAMPLE_3],
};

export const resolveRestartPlanDetailExample = (reason: string): string[] => {
  const code = getUnderuseReasonCode(reason)
  return RESTART_PLAN_DETAIL_EXAMPLE[code] || [];
}
